import React from "react"
import cx from "classnames"
import { PageSecondaryHeaderBreadcrumbProps } from "./page-secondary-header-breadcrumb"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderBreadcrumbsProps = {
  /** Page header breadcrumbs */
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderBreadcrumbsStyles = [
  "@container/breadcrumbs",
  // Optically align the first breadcrumb's text horizontally with other content
  "-ml-2",
  "col-span-full @3xl/page-header:col-span-1",
  "flex items-center"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header breadcrumbs.
 *
 * Used for backlink navigation. The last breadcrumb is automatically marked as the
 * current breadcrumb and renders as static text.
 *
 * Below `@min-[22rem]/breadcrumbs` the breadcrumbs collapse responsively: the first
 * breadcrumb becomes a back button, interior breadcrumbs are hidden, and the
 * last (current) breadcrumb remains as the only visible text breadcrumb with no
 * leading divider.
 */
export function PageSecondaryHeaderBreadcrumbs({
  children,
  className
}: PageSecondaryHeaderBreadcrumbsProps) {
  const items = React.Children.toArray(children)

  return (
    <div
      className={cx(pageSecondaryHeaderBreadcrumbsStyles, className)}
      data-slot="secondary-breadcrumbs"
    >
      {items.map((child, i) => {
        if (!React.isValidElement<PageSecondaryHeaderBreadcrumbProps>(child)) {
          return child
        }

        const isFirst = i === 0
        const isLast = i === items.length - 1

        // Below `@min-[22rem]/breadcrumbs` the first breadcrumb becomes a back button,
        // interior breadcrumbs are hidden, and the last breadcrumb is the only
        // visible text breadcrumb.
        return React.cloneElement(child, {
          isFirst,
          isCurrent: isLast,
          isInterior: !isFirst && !isLast
        })
      })}
    </div>
  )
}
